import { useEffect, useMemo } from 'react';
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet } from 'react-native';

import { iosVerticalScrollContentContainerStyle } from '@fhs/ui';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { CHECKOUT_SCROLL_VIEW } from '@fhs-legacy/frontend/src/utils/test-ids';

import CheckoutActionButton from '../components/checkout-components/checkout-action-button';
import CheckoutSavingsBanner from '../components/checkout-components/checkout-savings-banner';
import { CheckoutOrderSummary } from '../components/checkout-components/order-summary';
import CheckoutPaymentDetails from '../components/checkout-components/payment-details';
import ServiceModeDetails from '../components/checkout-components/service-mode-details';
import { useReevaluateOffersOnPickupTimeChange } from '../components/checkout-components/service-mode-details/pickup/action-pickup-time-selector/use-reevaluate-offers-on-pickup-time-change';
import { usePickupTimeOptions } from '../components/checkout-components/service-mode-details/pickup/pickup-time-options-context';

/**
 * Checkout scroll area. On Android, per-screen adjustPan is set in checkout.tsx
 * (useCheckoutAndroidKeyboardMode) so the window pans instead of resizing and the ScrollView does not jump.
 * On iOS we wrap in KeyboardAvoidingView.
 */
export function CheckoutScreen() {
  const { fireOrderIn, getCurrentFireOrderIn } = useOrderContext();
  const { displayRefreshTick } = usePickupTimeOptions();
  const reevaluateOffersOnPickupTimeChange = useReevaluateOffersOnPickupTimeChange();

  const pickUpTime = useMemo(() => {
    void displayRefreshTick;
    const secondsUntilPickup = getCurrentFireOrderIn?.() ?? fireOrderIn ?? 0;
    if (secondsUntilPickup <= 0) {
      return undefined;
    }
    return new Date(Date.now() + secondsUntilPickup * 1000).toISOString();
  }, [fireOrderIn, getCurrentFireOrderIn, displayRefreshTick]);

  useEffect(() => {
    reevaluateOffersOnPickupTimeChange(pickUpTime);
  }, [pickUpTime, reevaluateOffersOnPickupTimeChange]);

  const scrollContent = (
    <ScrollView
      testID={CHECKOUT_SCROLL_VIEW}
      style={styles.scrollView}
      contentContainerStyle={[styles.scrollContent, iosVerticalScrollContentContainerStyle]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      <ServiceModeDetails />
      <CheckoutPaymentDetails />
      <CheckoutOrderSummary />
      <CheckoutSavingsBanner />
    </ScrollView>
  );

  return (
    <>
      {Platform.OS === 'ios' ? (
        <KeyboardAvoidingView
          style={styles.keyboardAvoid}
          behavior="padding"
          keyboardVerticalOffset={0}
        >
          {scrollContent}
        </KeyboardAvoidingView>
      ) : (
        scrollContent
      )}
      <CheckoutActionButton />
    </>
  );
}

const styles = StyleSheet.create({
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
});
